/** Shared variables for GET_NOTIFICATIONS — recentCutoff is required by Hasura. */
export const getNotificationQueryVariables = (userId: string) => {
  const todayDate = new Date().toISOString();
  const recentCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  return {
    todayDate,
    recentCutoff,
    userId,
    pageSize: 2000,
    pageOffset: 0,
  };
};

type NotificationFeedSlice = {
  notification?: string | null;
};

type NotificationGlobalSlice = {
  notificationCount?: { recent?: number; past?: number };
  notification?: {
    reSettingNotificationCount?: boolean;
    newNotification?: string;
    lastAlertedFeedMs?: number;
  };
};

type MergeNotificationOptions = {
  /** Set baseline timestamp on load without playing sound/toast. */
  baselineOnly?: boolean;
};

const getNewestFeedTimestampMs = (
  notificationData: { current?: NotificationFeedSlice[]; past?: NotificationFeedSlice[] } | null | undefined,
): number => {
  const items = [...(notificationData?.current || []), ...(notificationData?.past || [])];
  return items.reduce((max, item) => {
    const ms = item?.notification ? new Date(item.notification).getTime() : NaN;
    return Number.isFinite(ms) && ms > max ? ms : max;
  }, 0);
};

/** Merge GET_NOTIFICATIONS results; alert only when a newer feed arrives than last alerted. */
export const mergeNotificationQueryIntoGlobalState = <T extends NotificationGlobalSlice>(
  globalState: T,
  notificationData: { current?: NotificationFeedSlice[]; past?: NotificationFeedSlice[] } | null | undefined,
  options?: MergeNotificationOptions,
): T => {
  const tempData = { ...globalState };
  const newRecent = (notificationData?.current || []).length;
  const newPast = (notificationData?.past || []).length;
  const newestFeedMs = getNewestFeedTimestampMs(notificationData);
  const lastAlertedFeedMs = globalState.notification?.lastAlertedFeedMs || 0;
  const hasNewFeed = newestFeedMs > lastAlertedFeedMs;

  tempData.notificationCount = {
    ...globalState.notificationCount,
    recent: newRecent,
    past: newPast,
  };

  tempData.notification = {
    ...globalState.notification,
    reSettingNotificationCount: !globalState.notification?.reSettingNotificationCount,
  };

  if (options?.baselineOnly) {
    if (newestFeedMs > 0) {
      tempData.notification.lastAlertedFeedMs = newestFeedMs;
    }
  } else if (hasNewFeed) {
    tempData.notification.newNotification = new Date().toISOString();
    tempData.notification.lastAlertedFeedMs = newestFeedMs;
  }

  return tempData;
};

/** Re-fetch notifications shortly after an action so new feeds are detected reliably. */
export const scheduleNotificationSyncRetries = (
  sync: () => void,
  delaysMs: number[] = [0, 800, 2000, 4000],
) => {
  delaysMs.forEach((delay) => {
    window.setTimeout(sync, delay);
  });
};
